import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import styles from './Venue.module.css';
import { EventModel, VenuePageData } from '../models/EventModel';
import EventListRow from '../components/EventListRow';
import ExploreMoreLinks from '../components/ExploreMoreLinks';
import SubscriptionSection from '../components/SubscriptionSection';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { buildVenuePageSeo } from '../utils/eventSeo';

const defaultTimezone = 'America/Toronto';
const emptyPreloadedEvents: EventModel[] = [];
const VenueMiniMap = lazy(() => import('../components/VenueMiniMap'));

function getTodayInTimezone(timezone: string): string {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const parts = formatter.formatToParts(new Date());
    const year = parts.find((part) => part.type === 'year')?.value ?? '';
    const month = parts.find((part) => part.type === 'month')?.value ?? '';
    const day = parts.find((part) => part.type === 'day')?.value ?? '';

    return `${year}-${month}-${day}`;
}

export default function Venue() {
    const { slug } = useParams();
    const venueSlug = slug || "";
    const preloadedEvents = window.__VENUE_EVENTS__ ?? emptyPreloadedEvents;
    const preloadedVenue = window.__VENUE__ ?? null;
    const hasMatchingPreload = preloadedVenue?.slug === venueSlug;
    const [events, setEvents] = useState<EventModel[]>(hasMatchingPreload ? preloadedEvents : []);
    const [venue, setVenue] = useState<VenuePageData | null>(hasMatchingPreload ? preloadedVenue : null);
    const [loading, setLoading] = useState<boolean>(!hasMatchingPreload);
    const [eventsError, setEventsError] = useState('');
    const [isMapOpen, setIsMapOpen] = useState(false);

    useEffect(() => {
        setIsMapOpen(false);
    }, [venueSlug]);

    useEffect(() => {
        let cancelled = false;

        async function fetchVenuePage() {
            if (!venueSlug) {
                setVenue(null);
                setEvents([]);
                setEventsError('');
                setLoading(false);
                return;
            }

            setLoading(true);
            setVenue(null);
            setEvents([]);
            setEventsError('');
            try {
                const venueResponse = await fetch(`/api/events/venues/${encodeURIComponent(venueSlug)}`);
                if (!venueResponse.ok) {
                    throw new Error('Lieu introuvable.');
                }

                const venueData = await venueResponse.json() as VenuePageData;
                if (cancelled) return;
                setVenue(venueData);

                const startDate = getTodayInTimezone(defaultTimezone);
                const eventParams = new URLSearchParams({
                    venue: venueSlug,
                    timezone: defaultTimezone,
                    date: startDate,
                    upcoming: '1',
                    limit: '50',
                    distinctPrograms: '1'
                });
                try {
                    const eventsResponse = await fetch(`/api/events?${eventParams.toString()}`);
                    if (!eventsResponse.ok) {
                        throw new Error("Impossible de charger les événements du lieu.");
                    }

                    const eventData = await eventsResponse.json() as EventModel[];
                    if (!cancelled) setEvents(Array.isArray(eventData) ? eventData.slice(0, 50) : []);
                } catch (eventError) {
                    console.error('Failed to load venue events:', eventError);
                    if (!cancelled) {
                        setEvents([]);
                        setEventsError("Impossible de charger les événements du lieu.");
                    }
                }
            } catch (error) {
                console.error('Failed to load venue:', error);
                if (!cancelled) {
                    setVenue(null);
                    setEvents([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        if (hasMatchingPreload) {
            setVenue(preloadedVenue);
            setEvents(preloadedEvents);
            setEventsError('');
            setLoading(false);
            return () => {
                cancelled = true;
            };
        }

        void fetchVenuePage();
        return () => {
            cancelled = true;
        };
    }, [venueSlug, hasMatchingPreload, preloadedEvents, preloadedVenue]);

    const venueName = venue?.name || venueSlug;
    const hasLatitudeValue = venue?.latitude !== null && venue?.latitude !== undefined && String(venue.latitude).trim() !== "";
    const hasLongitudeValue = venue?.longitude !== null && venue?.longitude !== undefined && String(venue.longitude).trim() !== "";
    const latitude = hasLatitudeValue ? Number(venue.latitude) : NaN;
    const longitude = hasLongitudeValue ? Number(venue.longitude) : NaN;
    const hasMapCoordinates = hasLatitudeValue && hasLongitudeValue && Number.isFinite(latitude) && Number.isFinite(longitude);
    const seo = useMemo(
        () => buildVenuePageSeo(venue, { baseUrl: location.origin, slug: venue?.slug ?? venueSlug }),
        [venue, venueSlug]
    );
    const sortedEvents = useMemo(
        () => [...events].sort((a, b) =>
            new Date(a.dates[0]?.date ?? 0).getTime() - new Date(b.dates[0]?.date ?? 0).getTime()
        ),
        [events]
    );
    const occurrenceDates = useMemo(
        () => sortedEvents
            .flatMap((event) => event.dates.map((dateEntry) => dateEntry.date))
            .filter(Boolean)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
        [sortedEvents]
    );

    const dayKey = (date: string) => new Intl.DateTimeFormat('en-CA', {
        timeZone: defaultTimezone,
        year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(new Date(date));
    const todayKey = getTodayInTimezone(defaultTimezone);
    const tomorrowValue = new Date(`${todayKey}T12:00:00Z`);
    tomorrowValue.setUTCDate(tomorrowValue.getUTCDate() + 1);
    const tomorrowKey = tomorrowValue.toISOString().slice(0, 10);
    const formatSectionDate = (date: string) => {
        const key = dayKey(date);
        if (key === todayKey) return 'Aujourd’hui';
        if (key === tomorrowKey) return 'Demain';
        return new Intl.DateTimeFormat('fr-CA', {
            timeZone: defaultTimezone,
            weekday: 'long', day: 'numeric', month: 'long'
        }).format(new Date(date));
    };
    const formatRangeDate = (date: string) => new Intl.DateTimeFormat('fr-CA', {
        timeZone: defaultTimezone,
        day: 'numeric', month: 'long', year: 'numeric'
    }).format(new Date(date));

    return <div className={styles.pageShell} style={{position: 'relative'}}>
        <Helmet>
            <title>{seo.title}</title>
            <meta name="description" content={seo.metaDescription} />
            <meta name="keywords" content={seo.keywords} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={seo.title} />
            <meta property="og:description" content={seo.metaDescription} />
            <meta property="og:url" content={seo.canonicalUrl} />
            {seo.imageUrl && <meta property="og:image" content={seo.imageUrl} />}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seo.title} />
            <meta name="twitter:description" content={seo.metaDescription} />
            {seo.imageUrl && <meta name="twitter:image" content={seo.imageUrl} />}
            <link rel="canonical" href={seo.canonicalUrl} />
        </Helmet>

        {venue && (
            <section className={`${styles.venueMeta} ${!venue.imageUrl ? styles.venueMetaNoImage : ''}`}>
                {venue.imageUrl && (
                    <img src={venue.imageUrl} alt={`Photo du lieu ${venue.name}`} className={styles.venueImage} />
                )}
                <div className={styles.venueDetails}>
                    {venue.venueType && <span className={styles.typePill}>{venue.venueType}</span>}
                    <h1 className={styles.venueHeading}>{venueName}</h1>
                    {venue.address && <p className={styles.addressLine}><strong>Adresse:</strong> {venue.address}</p>}
                    {venue.neighborhood && <Link
                        to={`/${encodeURIComponent(venue.neighborhood.city?.slug || 'quebec')}/${encodeURIComponent(venue.neighborhood.slug)}`}
                        className={styles.neighborhoodLink}
                    >
                        Découvrir d'autres lieux {venue.neighborhood.locativePhrase} <span aria-hidden="true">→</span>
                    </Link>}
                    {venue.description && <p className={styles.venueDescription}>{venue.description}</p>}
                    <div className={styles.actionsRow}>
                        {venue.websiteUrl && (
                            <a
                                href={venue.websiteUrl}
                                target="_blank"
                                rel="noreferrer"
                                className={styles.smallAction}
                                aria-label={`Visiter le site web de ${venue.name} dans un nouvel onglet`}
                            >
                                Site web <span aria-hidden="true">↗</span>
                            </a>
                        )}
                        {hasMapCoordinates && (
                            <button
                                type="button"
                                className={styles.smallAction}
                                onClick={() => setIsMapOpen((current) => !current)}
                                aria-expanded={isMapOpen}
                                aria-controls="venue-map-panel"
                            >
                                {isMapOpen ? 'Masquer la carte' : 'Voir sur la carte'}
                            </button>
                        )}
                    </div>

                    {hasMapCoordinates && isMapOpen && (
                        <div id="venue-map-panel" className={styles.mapSection}>
                            <div className={styles.mapFrame}>
                                <Suspense fallback={<div className={styles.mapFallback}>Chargement de la carte...</div>}>
                                    <VenueMiniMap
                                        latitude={latitude}
                                        longitude={longitude}
                                        title={venueName}
                                    />
                                </Suspense>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        )}

        {loading && <p>Chargement...</p>}

        {!loading && <section className={styles.scheduleHeading}>
            <h2 className={styles.eventsTitle}>Programmation à venir</h2>
            {sortedEvents.length > 0 && <p className={styles.scheduleSummary}>
                <strong>{sortedEvents.length}{sortedEvents.length === 50 ? ' événements affichés' : ` événement${sortedEvents.length > 1 ? 's' : ''}`}</strong>
                <span aria-hidden="true"> · </span>
                <span>{occurrenceDates.length} date{occurrenceDates.length > 1 ? 's' : ''}</span>
                {occurrenceDates.length > 0 && <>
                    <span aria-hidden="true"> · </span>
                    <span>
                        {dayKey(occurrenceDates[0]) === dayKey(occurrenceDates[occurrenceDates.length - 1])
                            ? `Le ${formatRangeDate(occurrenceDates[0])}`
                            : `Du ${formatRangeDate(occurrenceDates[0])} au ${formatRangeDate(occurrenceDates[occurrenceDates.length - 1])}`}
                    </span>
                </>}
            </p>}
        </section>}

        <div className={styles.eventsContainer}>
            {!loading && events.length == 0 && <div className={styles.emptyState}>
                <p>{eventsError || "Il n'y a aucun événement ou activité à venir pour l'instant."}</p>
                </div>}
            {sortedEvents.map((event, index) => {
                const eventDate = event.dates[0]?.date;
                const previousDate = sortedEvents[index - 1]?.dates[0]?.date;
                const startsNewDay = Boolean(eventDate) && (!previousDate || dayKey(eventDate) !== dayKey(previousDate));

                return <div className={styles.scheduleEntry} key={event.id}>
                    {startsNewDay && <h3 className={styles.dateSeparator}>{formatSectionDate(eventDate)}</h3>}
                    <EventListRow event={event} />
                </div>;
            })}
        </div>

        <ExploreMoreLinks wide links={[
            { to: '/ville-quebec/aujourdhui', label: "Voir quoi faire à Québec aujourd'hui", reloadDocument: true },
            { to: '/ville-quebec/evenements-cette-semaine', label: 'Voir tous les événements de cette semaine', reloadDocument: true },
            { to: '/#search', label: 'Rechercher parmi tous les événements' }
        ]} />
        <SubscriptionSection />
    </div>;
}
